import React from 'react';
import { getInitials, timeAgo, extractMessageContent, formatMessageText, getDisplayContact, getContactForChannel } from '../../utils/messageHelpers';
import { Message } from '../../types/wa_messages';

interface MessageItemProps {
  msg: Message;
  selectedConversation: any;
  setReplyingTo?: (msg: Message) => void;
  setPreviewImageUrl?: (url: string) => void;
  setToastMsg?: (msg: string | null) => void;
}

function AriaAvatar({ adminName }: { adminName?: string }) {
  if (adminName) {
    // Show admin initials with tooltip
    const initials = adminName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    return (
      <div 
        className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg select-none cursor-help"
        title={adminName}
      >
        {initials}
      </div>
    );
  }
  
  // Show ARiA logo for crewai agent messages
  return (
    <div className="w-10 h-10 rounded-full flex items-center justify-center">
      <img src="/logo192.png" alt="ARIA" className="w-7 h-7 object-cover rounded-full" />
    </div>
  );
}

// Extract common message content rendering logic
function MessageContent({ 
  extracted, 
  isPdf, 
  setPreviewImageUrl, 
  setReplyingTo, 
  msg 
}: { 
  extracted: any; 
  isPdf: boolean; 
  setPreviewImageUrl?: (url: string) => void; 
  setReplyingTo?: (msg: Message) => void; 
  msg: Message; 
}) {
  return (
    <div className="flex flex-col w-full">
      {extracted.type === 'image' && (extracted.previewUrl || extracted.imageUrl) ? (
        <div className="rounded-2xl bg-slate-100 p-2 cursor-pointer relative" onClick={() => setPreviewImageUrl && setPreviewImageUrl(extracted.previewUrl || extracted.imageUrl)}>
          <div className="flex flex-col items-start">
            <div className="relative">
              <img
                src={extracted.previewUrl || extracted.imageUrl}
                alt="attachment"
                className="rounded-lg max-h-48 object-contain"
                style={{ maxWidth: 240 }}
              />
              <a
                href={extracted.previewUrl || extracted.imageUrl}
                target="_blank"
                rel="noopener noreferrer"
                download={extracted.documentName || 'image'}
                className="absolute bottom-2 right-2 w-10 h-10 flex items-center justify-center rounded-full bg-white bg-opacity-90 border border-blue-200 text-blue-300 hover:text-blue-800 shadow-lg transition"
                title="Download image"
                onClick={e => e.stopPropagation()}
                style={{ zIndex: 2 }}
              >
                <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
                  <path d="M12 4v12m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  <rect x="4" y="20" width="16" height="2" rx="1" fill="currentColor" opacity=".1" />
                </svg>
              </a>
            </div>
            {extracted.text && (
              <div className="mt-2 px-4 py-2 rounded-2xl text-sm font-normal text-white" style={{ background: '#4666c9', maxWidth: 240, wordBreak: 'break-word' }}>
                {/* Email Subject Line - inline before content */}
                {msg.channel === 'email' && extracted.emailSubject && (
                  <div className="border-white border-opacity-20">
                    <span className="text-sm font-medium opacity-90">Subject : {extracted.emailSubject}</span>
                  </div>
                )}
                {extracted.htmlContent ? (
                  <div 
                    dangerouslySetInnerHTML={{ __html: extracted.htmlContent }} 
                    style={{ color: 'white' }}
                    className="email-html-content"
                  />
                ) : (
                  formatMessageText(extracted.text)
                )}
              </div>
            )}
          </div>
          {/* Email Attachments for image messages */}
          {extracted.emailAttachments && extracted.emailAttachments.length > 0 && msg.channel === 'email' && (
            <div className="mt-2 flex flex-col gap-2">
              {extracted.emailAttachments.map((attachment: { filename: string; content: string; content_type?: string }, idx: number) => {
                const handleDownload = () => {
                  try {
                    const byteCharacters = atob(attachment.content);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                      byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: attachment.content_type || 'application/octet-stream' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = attachment.filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('Failed to download attachment:', error);
                  }
                };
                return (
                  <div key={idx} className="bg-white rounded-lg border px-3 py-2 flex items-center justify-between max-w-[240px]">
                    <span className="text-xs text-slate-700 truncate flex-1">{attachment.filename}</span>
                    <button
                      type="button"
                      className="ml-2 w-8 h-8 flex items-center justify-center rounded border border-slate-200 text-slate-400 hover:text-blue-600"
                      onClick={handleDownload}
                      title="Download"
                    >
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                        <path d="M12 4v12m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : extracted.type === 'document' ? (
        <div className="bg-white rounded-2xl border w-64 overflow-hidden max-w-[420px] flex flex-col">
          <div className="h-32 flex items-center justify-center rounded-t-2xl relative select-none bg-[#fff5f5]">
            {isPdf ? (
              <>
                <embed
                  src={extracted.documentUrl}
                  type="application/pdf"
                  width="100%"
                  height="128px"
                  style={{ borderRadius: '1rem', pointerEvents: 'none', userSelect: 'none' }}
                />
                <div
                  className="absolute inset-0"
                  style={{ background: 'rgba(255,255,255,0)', cursor: 'not-allowed', pointerEvents: 'all', zIndex: 2 }}
                  onContextMenu={e => e.preventDefault()}
                  onMouseDown={e => e.preventDefault()}
                  onClick={e => e.preventDefault()}
                  tabIndex={-1}
                />
              </>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <svg width="64" height="64" fill="none" viewBox="0 0 64 64">
                  <rect width="64" height="64" rx="12" fill="#f3f4f6" />
                  <path d="M22 12a4 4 0 0 0-4 4v32a4 4 0 0 0 4 4h20a4 4 0 0 0 4-4V27.656A4 4 0 0 0 45.656 24L34 12.828A4 4 0 0 0 31.172 12H22z" fill="#a3a3a3" />
                </svg>
                <span className="absolute text-lg font-semibold text-[#6b7280] mt-20" style={{ textShadow: '0 1px 4px #fff' }}>Document</span>
              </div>
            )}
          </div>
          <div className="px-4 py-2 flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-900 truncate">{extracted.documentName}</div>
              <div className="text-xs text-slate-400">{isPdf ? 'PDF Document' : 'Document'}</div>
            </div>
            {extracted.documentUrl && (
              <button
                type="button"
                className="ml-2 w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-blue-600 transition"
                title="Open document in new tab"
                onClick={e => {
                  e.stopPropagation();
                  window.open(extracted.documentUrl, '_blank', 'noopener,noreferrer');
                }}
              >
                <svg width="22" height="22" fill="none" viewBox="0 0 20 20"><path d="M10 3v10m0 0l-3-3m3 3l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><rect x="3" y="15" width="14" height="2" rx="1" fill="currentColor" opacity=".1" /></svg>
              </button>
            )}
          </div>
          {extracted.text && (
            <div className="inline-block mt-2 mx-4 mb-2 px-4 py-2 rounded-2xl text-sm font-normal text-white" style={{ background: '#4666c9', maxWidth: 240, wordBreak: 'break-word' }}>
              {/* Email Subject Line - inline before content */}
              {msg.channel === 'email' && extracted.emailSubject && (
                <div className="border-white border-opacity-20">
                  <span className="text-sm font-medium opacity-90">Subject : {extracted.emailSubject}</span>
                </div>
              )}
              {extracted.htmlContent ? (
                <div 
                  dangerouslySetInnerHTML={{ __html: extracted.htmlContent }} 
                  style={{ color: 'white' }}
                  className="email-html-content"
                />
              ) : (
                formatMessageText(extracted.text)
              )}
            </div>
          )}
          {/* Email Attachments for document messages */}
          {extracted.emailAttachments && extracted.emailAttachments.length > 0 && msg.channel === 'email' && (
            <div className="mx-4 mb-2 flex flex-col gap-2">
              {extracted.emailAttachments.map((attachment: { filename: string; content: string; content_type?: string }, idx: number) => {
                const handleDownload = () => {
                  try {
                    const byteCharacters = atob(attachment.content);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                      byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: attachment.content_type || 'application/octet-stream' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = attachment.filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('Failed to download attachment:', error);
                  }
                };
                return (
                  <div key={idx} className="bg-white rounded-lg border px-3 py-2 flex items-center justify-between max-w-[240px]">
                    <span className="text-xs text-slate-700 truncate flex-1">{attachment.filename}</span>
                    <button
                      type="button"
                      className="ml-2 w-8 h-8 flex items-center justify-center rounded border border-slate-200 text-slate-400 hover:text-blue-600"
                      onClick={handleDownload}
                      title="Download"
                    >
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                        <path d="M12 4v12m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : msg.quoted_wa_msg ? (
        <div className="flex flex-col w-80 max-w-full rounded-2xl overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}>
          <div className="flex items-center border-l-4 border-blue-400 bg-blue-50 p-2 pl-3" style={{ borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
            <div className="flex-1 min-w-0">
              {(() => {
                const quoted = msg.quoted_wa_msg;
                if (!quoted) return null;
                if (quoted.message_type === 'image' && quoted.backup_media_urls && quoted.backup_media_urls.length > 0) {
                  return (
                    <div className="flex items-center gap-2">
                      <img src={quoted.backup_media_urls[0]} alt="quoted-img" className="w-10 h-10 object-cover rounded border" />
                      <div className="flex flex-col min-w-0">
                        {quoted.content && <div className="text-xs text-slate-900">{quoted.content}</div>}
                        <div className="text-xs text-slate-400">Image</div>
                      </div>
                    </div>
                  );
                } else if (quoted.message_type === 'document' && quoted.backup_media_urls && quoted.backup_media_urls.length > 0) {
                  return (
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded border">
                        <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><rect width="24" height="24" rx="4" fill="#e0e7ff" /><path d="M7 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.828A2 2 0 0 0 18.414 8L14 3.586A2 2 0 0 0 12.586 3H7z" fill="#a3a3a3" /><text x="8" y="19" fontSize="8" fill="#a3a3a3" fontFamily="monospace">FILE</text></svg>
                      </div>
                      <div className="flex flex-col min-w-0">
                        {quoted.content && <div className="text-xs text-slate-900">{quoted.content}</div>}
                        {quoted.filename && <div className="text-xs text-slate-400 mt-1">{quoted.filename}</div>}
                      </div>
                    </div>
                  );
                } else if (quoted.message_type === 'text' && quoted.content) {
                  return <div className="text-xs text-slate-900">{quoted.content}</div>;
                } else {
                  return <div className="text-xs text-slate-400 italic">[No preview]</div>;
                }
              })()}
            </div>
          </div>
          <div className="px-4 py-2 text-sm font-normal leading-relaxed text-white" style={{ background: '#4666c9',borderBottomLeftRadius: 16, borderBottomRightRadius: 16, borderTopLeftRadius: 0, borderTopRightRadius: 0, fontFamily: 'inherit', wordBreak: 'break-word' }}>
            {/* Email Subject Line - inline before content */}
            {msg.channel === 'email' && extracted.emailSubject && (
              <div className="border-white border-opacity-20">
                <span className="text-sm font-medium opacity-90">Subject : {extracted.emailSubject}</span>
              </div>
            )}
            {extracted.htmlContent ? (
              <div 
                dangerouslySetInnerHTML={{ __html: extracted.htmlContent }} 
                style={{ color: 'white' }}
                className="email-html-content"
              />
            ) : (
              formatMessageText(extracted.text)
            )}
          </div>
          {/* Email Attachments for quoted messages */}
          {extracted.emailAttachments && extracted.emailAttachments.length > 0 && msg.channel === 'email' && (
            <div className="px-4 pb-2 flex flex-col gap-2">
              {extracted.emailAttachments.map((attachment: { filename: string; content: string; content_type?: string }, idx: number) => {
                const handleDownload = () => {
                  try {
                    const byteCharacters = atob(attachment.content);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                      byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: attachment.content_type || 'application/octet-stream' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = attachment.filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('Failed to download attachment:', error);
                  }
                };
                return (
                  <div key={idx} className="bg-white rounded-lg border px-3 py-2 flex items-center justify-between max-w-[240px]">
                    <span className="text-xs text-slate-700 truncate flex-1">{attachment.filename}</span>
                    <button
                      type="button"
                      className="ml-2 w-8 h-8 flex items-center justify-center rounded border border-slate-200 text-slate-400 hover:text-blue-600"
                      onClick={handleDownload}
                      title="Download"
                    >
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                        <path d="M12 4v12m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="rounded-2xl px-4 py-2 text-sm font-normal leading-relaxed text-white" style={{ background: '#4666c9', fontFamily: 'inherit', wordBreak: 'break-word' }}>
            {/* Email Subject Line - inline before content */}
            {msg.channel === 'email' && extracted.emailSubject && (
              <div className="border-white border-opacity-20">
                <span className="text-sm font-medium opacity-90">Subject : {extracted.emailSubject}</span>
              </div>
            )}
            {extracted.htmlContent ? (
              <div 
                dangerouslySetInnerHTML={{ __html: extracted.htmlContent }} 
                style={{ color: 'white' }}
                className="email-html-content"
              />
            ) : (
              formatMessageText(extracted.text)
            )}
          </div>
          {/* Email Attachments */}
          {extracted.emailAttachments && extracted.emailAttachments.length > 0 && (
            <div className="mt-2 flex flex-col gap-2">
              {extracted.emailAttachments.map((attachment: { filename: string; content: string; content_type?: string }, idx: number) => {
                const handleDownload = () => {
                  try {
                    // Convert base64 to blob
                    const byteCharacters = atob(attachment.content);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                      byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: attachment.content_type || 'application/octet-stream' });
                    
                    // Create download link
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = attachment.filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('Failed to download attachment:', error);
                  }
                };

                const isImage = attachment.content_type?.startsWith('image/');
                const isPdf = attachment.filename.toLowerCase().endsWith('.pdf');
                
                return (
                  <div key={idx} className="bg-white rounded-2xl border w-64 overflow-hidden max-w-[420px] flex flex-col">
                    {isImage ? (
                      <div className="h-32 flex items-center justify-center rounded-t-2xl relative bg-slate-100">
                        <img
                          src={`data:${attachment.content_type || 'image/png'};base64,${attachment.content}`}
                          alt={attachment.filename}
                          className="max-h-32 max-w-full object-contain"
                        />
                      </div>
                    ) : isPdf ? (
                      <div className="h-32 flex items-center justify-center rounded-t-2xl relative select-none bg-[#fff5f5]">
                        <embed
                          src={`data:application/pdf;base64,${attachment.content}`}
                          type="application/pdf"
                          width="100%"
                          height="128px"
                          style={{ borderRadius: '1rem', pointerEvents: 'none', userSelect: 'none' }}
                        />
                        <div
                          className="absolute inset-0"
                          style={{ background: 'rgba(255,255,255,0)', cursor: 'not-allowed', pointerEvents: 'all', zIndex: 2 }}
                          onContextMenu={e => e.preventDefault()}
                          onMouseDown={e => e.preventDefault()}
                          onClick={e => e.preventDefault()}
                          tabIndex={-1}
                        />
                      </div>
                    ) : (
                      <div className="h-32 flex items-center justify-center rounded-t-2xl relative select-none bg-slate-50">
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <svg width="64" height="64" fill="none" viewBox="0 0 64 64">
                            <rect width="64" height="64" rx="12" fill="#f3f4f6" />
                            <path d="M22 12a4 4 0 0 0-4 4v32a4 4 0 0 0 4 4h20a4 4 0 0 0 4-4V27.656A4 4 0 0 0 45.656 24L34 12.828A4 4 0 0 0 31.172 12H22z" fill="#a3a3a3" />
                          </svg>
                          <span className="absolute text-lg font-semibold text-[#6b7280] mt-20" style={{ textShadow: '0 1px 4px #fff' }}>Document</span>
                        </div>
                      </div>
                    )}
                    <div className="px-4 py-2 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-900 truncate">{attachment.filename}</div>
                        <div className="text-xs text-slate-400">
                          {isImage ? 'Image' : isPdf ? 'PDF Document' : attachment.content_type || 'Document'}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="ml-2 w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-blue-600 transition"
                        title="Download attachment"
                        onClick={handleDownload}
                      >
                        <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
                          <path d="M12 4v12m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          <rect x="4" y="20" width="16" height="2" rx="1" fill="currentColor" opacity=".1" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Extract quoted message component for incoming messages
function QuotedMessage({ msg, extracted }: { msg: Message; extracted: any }) {
  if (!msg.quoted_wa_msg) return null;
  
  return (
    <div className="mb-2 w-full flex flex-col">
      <div className="flex flex-col w-80 max-w-full rounded-2xl overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}>
        <div className="flex items-center border-l-4 border-blue-400 bg-blue-50 p-2 pl-3" style={{ borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
          <div className="flex-1 min-w-0">
            {(() => {
              const quoted = msg.quoted_wa_msg;
              if (!quoted) return null;
              if (quoted.message_type === 'image' && quoted.backup_media_urls && quoted.backup_media_urls.length > 0) {
                return (
                  <div className="flex items-center gap-2">
                    <img src={quoted.backup_media_urls[0]} alt="quoted-img" className="w-10 h-10 object-cover rounded border" />
                    <div className="flex flex-col min-w-0">
                      {quoted.content && <div className="text-xs text-slate-900">{quoted.content}</div>}
                      <div className="text-xs text-slate-400">Image</div>
                    </div>
                  </div>
                );
              }
              return (
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded border">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><rect width="24" height="24" rx="4" fill="#e0e7ff" /><path d="M7 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.828A2 2 0 0 0 18.414 8L14 3.586A2 2 0 0 0 12.586 3H7z" fill="#a3a3a3" /><text x="8" y="19" fontSize="8" fill="#a3a3a3" fontFamily="monospace">FILE</text></svg>
                  </div>
                  <div className="flex flex-col min-w-0">
                    {quoted.content && <div className="text-xs text-slate-900">{quoted.content}</div>}
                    {quoted.filename && <div className="text-xs text-slate-400 mt-1">{quoted.filename}</div>}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
        <div className="px-4 py-2 text-sm font-normal leading-relaxed text-white" style={{ background: '#4666c9',borderBottomLeftRadius: 16, borderBottomRightRadius: 16, borderTopLeftRadius: 0, borderTopRightRadius: 0, fontFamily: 'inherit', wordBreak: 'break-word' }}>
          {formatMessageText(extracted.text)}
        </div>
      </div>
    </div>
  );
}

const MessageItem: React.FC<MessageItemProps> = ({ msg, selectedConversation, setReplyingTo, setPreviewImageUrl, setToastMsg }) => {
  const extracted = extractMessageContent(msg);
  const isAgent = msg.direction === 'outbound';
  const isPdf = extracted.documentName?.toLowerCase().endsWith('.pdf');

  // Check if message can be quoted (only WhatsApp messages can be quoted)
  const canQuoteMessage = (message: Message): boolean => {
    // Don't show quote button for email messages
    if (message.channel === 'email') {
      return false;
    }
    return !!message.whatsapp_message_id;
  };

  // Handle reply button click
  const handleReplyClick = () => {
    if (!canQuoteMessage(msg)) {
      const errorMessage = "This message cannot be quoted because it doesn't have whatsapp_message_id."
      setToastMsg && setToastMsg(errorMessage);
      // Auto-clear the toast after 3 seconds
      setTimeout(() => setToastMsg && setToastMsg(null), 3000);
      return;
    }
    
    setReplyingTo && setReplyingTo(msg);
  };

  // Common reply button component (only show for WhatsApp messages)
  const ReplyButton = () => {
    // Don't show quote button if message channel is email
    if (msg.channel === 'email') {
      return null;
    }
    
    const canQuote = canQuoteMessage(msg);
    return (
      <button
        className={`opacity-0 group-hover:opacity-100 transition bg-white border rounded-full w-8 h-8 flex items-center justify-center shadow-sm ${isAgent ? 'mr-2' : 'ml-2'} ${
          canQuote 
            ? 'border-slate-200 text-slate-400 hover:text-blue-600' 
            : 'border-red-200 text-red-400 hover:text-red-600'
        }`}
        style={{ pointerEvents: 'auto' }}
        title={canQuote ? "Reply" : "Cannot quote this message"}
        onClick={handleReplyClick}
      >
        <svg width="18" height="18" fill="none" viewBox="0 0 20 20"><path d="M4 10h8a4 4 0 0 1 4 4v0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 10l4-4M4 10l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
    );
  };

  if (isAgent) {
    return (
      <div className="flex justify-end mb-2">
        <div className="flex items-start gap-2">
          {/* Message column */}
          <div className="flex flex-col items-end max-w-[420px] w-auto">
            <div className="flex items-center group">
              <ReplyButton />
              <MessageContent 
                extracted={extracted} 
                isPdf={isPdf} 
                setPreviewImageUrl={setPreviewImageUrl} 
                setReplyingTo={setReplyingTo} 
                msg={msg} 
              />
            </div>
            {/* Outbound footer: timestamp and plain channel hint (no chip, no click) under the bubble */}
            {(() => {
              const channel = msg.channel;
              const value = channel ? getContactForChannel(selectedConversation?.pic_contact, channel) : '';
              const hint = channel ? `${channel}${value ? ' - ' + value : ''}` : '';
              return (
                <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                  <span>{timeAgo(msg.timestamp)}</span>
                  {hint ? <span>· {hint}</span> : null}
                  <span className="ml-1">✓</span>
                </div>
              );
            })()}
          </div>
          {/* Avatar column */}
          <div className="self-start flex-shrink-0"><AriaAvatar adminName={msg.admin_name} /></div>
        </div>
      </div>
    );
  }

  // Incoming (user) message: left-aligned
  return (
    <div className="flex justify-start mb-6 relative">
      <div className="flex items-start gap-2">
        {/* Avatar column */}
        <div className="self-start flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg select-none">
            {selectedConversation ? getInitials(selectedConversation.pic_name || '', getDisplayContact(selectedConversation.pic_contact)) : ''}
          </div>
        </div>
        {/* Message column */}
        <div className="flex flex-col items-start max-w-[420px] w-auto">
          <div className="flex items-center group">
            {/* <QuotedMessage msg={msg} extracted={extracted} /> */}
            <MessageContent 
              extracted={extracted} 
              isPdf={isPdf} 
              setPreviewImageUrl={setPreviewImageUrl} 
              setReplyingTo={setReplyingTo} 
              msg={msg} 
            />
            <ReplyButton />
          </div>
          {/* Inbound footer: timestamp and plain channel hint (same as outbound) */}
          {(() => {
            const channel = msg.channel;
            const value = channel ? getContactForChannel(selectedConversation?.pic_contact, channel) : '';
            const hint = channel ? `${channel}${value ? ' - ' + value : ''}` : '';
            return (
              <div className="mt-1 flex items-center gap-2 w-full text-xs text-slate-400">
                <span>{timeAgo(msg.timestamp)}</span>
                {hint ? <span>· {hint}</span> : null}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default MessageItem; 